import { useEffect, useState } from "react";
import { Loader2, Building2, Save } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const EMPTY = {
  company_name: "", contact_name: "", email: "", phone: "", siren: "", siret: "", tva_intra: "",
  address: "", postal_code: "", city: "", country: "France", payment_terms: "30 jours", notes: "",
};
const INPUT = "bg-[#161020] border-white/10 text-white text-sm h-9";

function Field({ label, k, form, set, testId, className = "", type = "text", placeholder = "" }) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">{label}</label>
      <Input type={type} value={form[k] || ""} onChange={e => set(k, e.target.value)} placeholder={placeholder} className={INPUT} data-testid={testId} />
    </div>
  );
}

export default function ClientFormDialog({ open, onClose, api, client, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEdit = !!client?.user_id;

  useEffect(() => {
    if (!open) return;
    setError("");
    setForm(client ? { ...EMPTY, ...client, email: client.email || client.billing_email || "" } : EMPTY);
  }, [open, client]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.company_name && !form.contact_name) { setError("Raison sociale ou nom du contact requis"); return; }
    setSaving(true); setError("");
    try {
      const r = isEdit ? await api.put(`/facturation/clients/${client.user_id}`, form) : await api.post("/facturation/clients", form);
      onSaved?.(r.data);
      onClose();
    } catch (e) { setError(e.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#f7c935]" /> {isEdit ? "Modifier le client" : "Nouveau client de facturation"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Fiche de facturation sans compte portail. Vous pourrez activer l'accès client plus tard depuis l'onglet Clients.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <Field label="Raison sociale" k="company_name" form={form} set={set} testId="client-company-input" className="sm:col-span-2" placeholder="Garage Dupont SARL" />
          <Field label="Contact" k="contact_name" form={form} set={set} testId="client-contact-input" placeholder="Prénom Nom" />
          <Field label="E-mail de facturation" k="email" form={form} set={set} testId="client-email-input" type="email" placeholder="compta@client.fr" />
          <Field label="Téléphone" k="phone" form={form} set={set} testId="client-phone-input" />
          <Field label="Conditions de paiement" k="payment_terms" form={form} set={set} testId="client-terms-input" placeholder="30 jours" />
          <Field label="SIREN" k="siren" form={form} set={set} testId="client-siren-input" />
          <Field label="SIRET" k="siret" form={form} set={set} testId="client-siret-input" />
          <Field label="TVA intracommunautaire" k="tva_intra" form={form} set={set} testId="client-tva-input" className="sm:col-span-2" placeholder="FR12345678901" />
          <Field label="Adresse" k="address" form={form} set={set} testId="client-address-input" className="sm:col-span-2" />
          <Field label="Code postal" k="postal_code" form={form} set={set} testId="client-zip-input" />
          <Field label="Ville" k="city" form={form} set={set} testId="client-city-input" />
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Notes internes</label>
            <Textarea rows={2} value={form.notes || ""} onChange={e => set("notes", e.target.value)} className="bg-[#161020] border-white/10 text-white text-sm" data-testid="client-notes-input" />
          </div>
        </div>
        {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2" data-testid="client-form-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-slate-400 h-9 text-xs">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold h-9 text-xs" data-testid="client-form-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} {isEdit ? "Enregistrer" : "Créer le client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
